import { useCallback, useEffect, useState } from 'react';

export interface PIPState {
  height: number;
  minimized: boolean;
  width: number;
  x: number;
  y: number;
}

const STORAGE_KEY = 'liveSessionsMonitor.pip';
const DEFAULT_WIDTH = 420;
const DEFAULT_HEIGHT = 560;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 240;
const RIGHT_MARGIN = 24;
const BOTTOM_MARGIN = 24;

// AIDEV-NOTE: Default to the bottom-right corner of the viewport, falling back to (0, 0)
// during SSR / tests where `window` may not have real dimensions.
const defaultPosition = () => {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  return {
    x: Math.max(0, window.innerWidth - DEFAULT_WIDTH - RIGHT_MARGIN),
    y: Math.max(0, window.innerHeight - DEFAULT_HEIGHT - BOTTOM_MARGIN),
  };
};

const readStored = (): Partial<PIPState> | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeStored = (state: PIPState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage full or unavailable — ignore
  }
};

const initialState = (): PIPState => {
  const stored = readStored() ?? {};
  const { x, y } = defaultPosition();
  return {
    height: typeof stored.height === 'number' ? Math.max(MIN_HEIGHT, stored.height) : DEFAULT_HEIGHT,
    minimized: stored.minimized === true,
    width: typeof stored.width === 'number' ? Math.max(MIN_WIDTH, stored.width) : DEFAULT_WIDTH,
    x: typeof stored.x === 'number' ? stored.x : x,
    y: typeof stored.y === 'number' ? stored.y : y,
  };
};

const useSessionDetailPIP = () => {
  const [state, setState] = useState<PIPState>(initialState);

  // AIDEV-NOTE: writeStored is a synchronous localStorage.setItem on every state change. This
  // stays cheap only because setPosition/setSize are wired to Draggable/Resizable `onStop` (drag
  // end), not `onDrag`/live resize. Don't move them to per-frame handlers without debouncing here.
  useEffect(() => { writeStored(state); }, [state]);

  const setPosition = useCallback((x: number, y: number) => {
    setState(prev => ({ ...prev, x, y }));
  }, []);

  const setSize = useCallback((width: number, height: number) => {
    setState(prev => ({ ...prev, height, width }));
  }, []);

  const toggleMinimized = useCallback(() => {
    setState(prev => ({ ...prev, minimized: !prev.minimized }));
  }, []);

  return { setPosition, setSize, state, toggleMinimized };
};

export default useSessionDetailPIP;
export { DEFAULT_HEIGHT, DEFAULT_WIDTH, MIN_HEIGHT, MIN_WIDTH };
